import "@/App.css";
import { useState, useEffect } from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import axios from "axios";
import Layout from "@/components/Layout";
import Dashboard from "@/pages/Dashboard";
import Hospitals from "@/pages/Hospitals";
import Devices from "@/pages/Devices";
import Reports from "@/pages/Reports";
import Biovics from "@/pages/Biovics";
import Login from "@/pages/Login";
import UserManagement from "@/pages/UserManagement";
import Dealers from "@/pages/Dealers";
import Finance from "@/pages/Finance";
import ProductInventory from "@/pages/ProductInventory";
import { Toaster } from "@/components/ui/sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Protected Route component
const ProtectedRoute = ({ children, user, allowedPages, pageId }) => {
  if (!user) {
    return <Navigate to="/login" replace />;
  }
  
  // Admin can access everything
  if (user.is_admin) {
    return children;
  }
  
  // Check if user has access to this page
  if (pageId && !allowedPages.includes(pageId)) {
    return <Navigate to="/" replace />;
  }
  
  return children;
};

function App() {
  const [user, setUser] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    // Check for existing token on app load
    const token = localStorage.getItem("token");
    const savedUser = localStorage.getItem("user");
    
    if (token && savedUser) {
      axios.defaults.headers.common["Authorization"] = `Bearer ${token}`;
      
      // Verify token is still valid
      axios.get(`${API}/auth/me`)
        .then((response) => {
          setUser(response.data);
          localStorage.setItem("user", JSON.stringify(response.data));
        })
        .catch(() => {
          // Token invalid, clear storage
          localStorage.removeItem("token");
          localStorage.removeItem("user");
          delete axios.defaults.headers.common["Authorization"];
        })
        .finally(() => {
          setIsLoading(false);
        });
    } else {
      setIsLoading(false);
    }
  }, []);

  const handleLogin = (userData) => {
    setUser(userData);
  };

  const handleLogout = () => {
    localStorage.removeItem("token");
    localStorage.removeItem("user");
    delete axios.defaults.headers.common["Authorization"];
    setUser(null);
  };

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto"></div>
          <p className="mt-4 text-muted-foreground">Yükleniyor...</p>
        </div>
      </div>
    );
  }

  const allowedPages = user?.is_admin 
    ? ["dashboard", "hospitals", "devices", "reports", "biovics", "users", "dealers", "finans", "products"] 
    : (user?.allowed_pages || []);

  return (
    <div className="App">
      <BrowserRouter>
        <Routes>
          <Route path="/login" element={
            user ? <Navigate to="/" replace /> : <Login onLogin={handleLogin} />
          } />
          
          <Route path="/" element={
            <ProtectedRoute user={user} allowedPages={allowedPages} pageId="dashboard">
              <Layout user={user} onLogout={handleLogout} allowedPages={allowedPages} />
            </ProtectedRoute>
          }>
            <Route index element={<Dashboard />} />
            <Route path="hospitals" element={
              <ProtectedRoute user={user} allowedPages={allowedPages} pageId="hospitals">
                <Hospitals />
              </ProtectedRoute>
            } />
            <Route path="devices" element={
              <ProtectedRoute user={user} allowedPages={allowedPages} pageId="devices">
                <Devices />
              </ProtectedRoute>
            } />
            <Route path="reports" element={
              <ProtectedRoute user={user} allowedPages={allowedPages} pageId="reports">
                <Reports />
              </ProtectedRoute>
            } />
            <Route path="biovics" element={
              <ProtectedRoute user={user} allowedPages={allowedPages} pageId="biovics">
                <Biovics />
              </ProtectedRoute>
            } />
            <Route path="users" element={
              <ProtectedRoute user={user} allowedPages={allowedPages} pageId="users">
                <UserManagement />
              </ProtectedRoute>
            } />
            <Route path="dealers" element={
              <ProtectedRoute user={user} allowedPages={allowedPages} pageId="dealers">
                <Dealers />
              </ProtectedRoute>
            } />
            <Route path="finans" element={
              <ProtectedRoute user={user} allowedPages={allowedPages} pageId="finans">
                <Finance />
              </ProtectedRoute>
            } />
            <Route path="products" element={
              <ProtectedRoute user={user} allowedPages={allowedPages} pageId="products">
                <ProductInventory />
              </ProtectedRoute>
            } />
          </Route>
        </Routes>
      </BrowserRouter>
      <Toaster />
    </div>
  );
}

export default App;
